import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@tonic/shared/components/ui';
import { ArrowUpRight, ArrowDownRight, Calendar, Leaf } from 'lucide-react';
import { useEnvironmentMetrics, useResaleEnvironment } from '@/features/re-commerce/api/hooks';
import { formatNumber, formatPercent } from '@/utils/format';
import { ChartWrapper } from '@/features/shared/components/data-display/ChartWrapper';

const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '12m', label: 'Last 12 Months' },
];

export function ResaleEnvironmentPage() {
  const [timeRange, setTimeRange] = React.useState('30d');
  const { data, isLoading } = useEnvironmentMetrics({ timeRange });
  const { data: environmentData } = useResaleEnvironment();

  // Extract metrics from the data object, providing default values if undefined
  const metrics = data?.environmentMetrics || {
    carbonSavings: { value: 0, trend: 0, change: 0 },
    waterSaved: { value: 0, trend: 0, change: 0 },
    materialsSaved: { value: 0, trend: 0, change: 0 },
    landfillAvoided: { value: 0, trend: 0, change: 0 },
    carbonSavingsChart: [],
    impactByCategory: [],
    equivalencies: { trees: 0, cars: 0, homes: 0 },
    goals: []
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 w-48 bg-neutral-800 animate-pulse rounded" />
          <div className="h-10 w-40 bg-neutral-800 animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="bg-neutral-800 animate-pulse h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="bg-neutral-800 animate-pulse h-[400px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Leaf className="w-8 h-8 text-green-500" />
          <div>
            <h1 className="text-2xl font-bold">Environmental Impact</h1>
            <p className="text-sm text-neutral-500">Track the environmental benefits of your resale program</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-neutral-500" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Carbon Savings</CardTitle>
            <Badge variant={metrics.carbonSavings.trend > 0 ? 'secondary' : 'destructive'}>
              {metrics.carbonSavings.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(Math.abs(metrics.carbonSavings.trend))}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(metrics.carbonSavings.value)}mt</div>
            <p className="text-xs text-neutral-500">CO₂ emissions saved</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Water Saved</CardTitle>
            <Badge variant={metrics.waterSaved.trend > 0 ? 'secondary' : 'destructive'}>
              {metrics.waterSaved.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(Math.abs(metrics.waterSaved.trend))}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(metrics.waterSaved.value)}L</div>
            <p className="text-xs text-neutral-500">Water consumption avoided</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Materials Saved</CardTitle>
            <Badge variant={metrics.materialsSaved.trend > 0 ? 'secondary' : 'destructive'}>
              {metrics.materialsSaved.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(Math.abs(metrics.materialsSaved.trend))}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(metrics.materialsSaved.value)}kg</div>
            <p className="text-xs text-neutral-500">Raw materials conserved</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Landfill Avoided</CardTitle>
            <Badge variant={metrics.landfillAvoided.trend > 0 ? 'secondary' : 'destructive'}>
              {metrics.landfillAvoided.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {formatPercent(Math.abs(metrics.landfillAvoided.trend))}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(metrics.landfillAvoided.value)}kg</div>
            <p className="text-xs text-neutral-500">Waste diverted from landfill</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartWrapper
          title="Carbon Savings Over Time"
          type="line"
          data={metrics.carbonSavingsChart}
          dataKey="value"
          xAxisKey="date"
          height={300}
        />

        <ChartWrapper
          title="Environmental Impact by Category"
          type="bar"
          data={metrics.impactByCategory}
          dataKey="carbonSaved"
          xAxisKey="category"
          height={300}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Environmental Equivalencies</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="text-sm font-medium text-neutral-500">Trees Planted Equivalent</div>
              <div className="text-2xl font-bold">{formatNumber(metrics.equivalencies.trees)} trees</div>
              <div className="text-xs text-neutral-500">Based on annual CO₂ absorption</div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-neutral-500">Cars Off Road</div>
              <div className="text-2xl font-bold">{formatNumber(metrics.equivalencies.cars)} cars</div>
              <div className="text-xs text-neutral-500">Annual emissions avoided</div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-neutral-500">Homes Powered</div>
              <div className="text-2xl font-bold">{formatNumber(metrics.equivalencies.homes)} homes</div>
              <div className="text-xs text-neutral-500">Annual energy savings</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sustainability Goals Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {metrics.goals.map((goal) => (
              <div key={goal.id} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="font-medium">{goal.name}</div>
                  <Badge variant={goal.status === 'on_track' ? 'secondary' : 'destructive'}>
                    {goal.status === 'on_track' ? 'On Track' : 'Needs Attention'}
                  </Badge>
                </div>
                <div className="w-full bg-neutral-800 rounded-full h-2">
                  <div
                    className="bg-green-500 rounded-full h-2"
                    style={{ width: `${goal.progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-neutral-500">
                  <div>Current: {formatNumber(goal.current)} {goal.unit}</div>
                  <div>Target: {formatNumber(goal.target)} {goal.unit}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Carbon Savings</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartWrapper
            title="Carbon Savings"
            type="line"
            data={environmentData?.carbonSavings || []}
            dataKey="value"
            xAxisKey="date"
            height={300}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Material Recovery</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartWrapper
            title="Material Recovery"
            type="bar"
            data={environmentData.materialRecovery}
            dataKey="amount"
            xAxisKey="material"
            height={350}
          />
        </CardContent>
      </Card>
    </div>
  );
}
